import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Search, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { LocalizedName } from '@/components/localized-name';
import { initials } from '@/lib/format';
import { useAssignGift, useUserSearch } from './gifts.api';
import type { SubscriptionGift } from './types';

interface FoundUser {
  id: string;
  firstName: string;
  lastName: string;
  completeName?: string;
  email: string;
  avatar?: string | null;
}

/**
 * Attach an unassigned gift to a real user account.
 *
 * Two bugs in the original, both fatal to the feature:
 *  - the Assign button's handler was `() => setSelectedGift`, which returns the
 *    setter rather than calling it, so the modal never opened at all;
 *  - the modal wrapped the search box in an antd `Dropdown`, which meant the
 *    results list opened on click rather than on having results.
 */
export function AssignGiftDialog({
  gift,
  open,
  onOpenChange,
}: {
  gift: SubscriptionGift | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');
  const [selected, setSelected] = useState<FoundUser | null>(null);

  const search = useUserSearch(term, open);
  const assign = useAssignGift();

  // A different gift is a different recipient; nothing should carry over.
  useEffect(() => {
    setSelected(null);
    setDraft('');
    setTerm('');
  }, [gift?.id]);

  if (!gift) return null;

  const results = (search.data?.results ?? []) as FoundUser[];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('gifts.assignMembership')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('gifts.assignBody', {
              recipient: gift.recipientFullName ?? gift.recipientEmail ?? '',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          {gift.subscription && (
            <div className="rounded-lg border p-2">
              <LocalizedName
                nameEn={gift.subscription.nameEn}
                nameEs={gift.subscription.nameEs}
                internalName={gift.subscription.internalName}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs font-semibold">{t('gifts.user')}</Label>

            {selected ? (
              <div className="flex items-center gap-2 rounded-lg border p-2">
                <Avatar className="size-8 shrink-0">
                  {selected.avatar && <AvatarImage src={selected.avatar} alt="" />}
                  <AvatarFallback>
                    {initials(selected.firstName, selected.lastName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1 text-sm">
                  <p className="truncate font-medium">
                    {selected.completeName ??
                      [selected.firstName, selected.lastName]
                        .filter(Boolean)
                        .join(' ')}
                  </p>
                  <p className="text-muted-foreground truncate text-xs">
                    {selected.email}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('gifts.remove')}
                  onClick={() => setSelected(null)}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder={t('gifts.searchUser')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        setTerm(draft);
                      }
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={() => setTerm(draft)}
                    disabled={!draft.trim() || search.isFetching}
                  >
                    {search.isFetching ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      <Search className="size-4" />
                    )}
                  </Button>
                </div>

                {search.isLoading && (
                  <div className="divide-y rounded-lg border">
                    {Array.from({ length: 3 }).map((_, index) => (
                      <div key={index} className="space-y-1.5 p-2">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-52" />
                      </div>
                    ))}
                  </div>
                )}

                {term && search.isSuccess && results.length === 0 && (
                  <p className="text-muted-foreground text-xs">
                    {t('gifts.noUsers')}
                  </p>
                )}

                {results.length > 0 && (
                  <div className="max-h-48 divide-y overflow-y-auto rounded-lg border">
                    {results.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => {
                          setSelected(user);
                          setDraft('');
                          setTerm('');
                        }}
                        className="hover:bg-accent w-full p-2 text-left text-sm transition-colors"
                      >
                        <p className="font-medium">
                          {user.completeName ??
                            [user.firstName, user.lastName]
                              .filter(Boolean)
                              .join(' ')}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {user.email}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>{t('orders.close')}</AlertDialogCancel>
          <AlertDialogAction
            disabled={!selected || assign.isPending}
            onClick={() =>
              assign.mutate(
                { giftId: gift.id, recipientUserId: selected!.id },
                {
                  onSuccess: () => {
                    toast.success(t('gifts.assignedToast'));
                    onOpenChange(false);
                  },
                  onError: (error) =>
                    toast.error(
                      error instanceof Error
                        ? error.message
                        : t('gifts.assignFailed')
                    ),
                }
              )
            }
          >
            {assign.isPending ? (
              <Loader2 className="animate-spin" />
            ) : (
              t('gifts.assign')
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
